<script setup lang="ts">
/**
 * 通知下拉组件
 * 显示在 Header 右上角，包含铃铛图标、未读角标和通知列表
 */
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { 
  Dropdown, 
  Badge, 
  Button, 
  List, 
  Empty, 
  Spin,
  Modal,
  message 
} from 'ant-design-vue';
import {
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  MessageOutlined,
  NotificationOutlined,
} from '@ant-design/icons-vue';
import { useNotificationsStore } from '@/stores/notifications';
import { useUserStore } from '@/stores/user';
import type { NotificationItem, NotificationType } from '@/api/notifications';
import { formatDistanceToNow, format } from 'date-fns';
import { zhCN } from 'date-fns/locale';

const router = useRouter();
const notificationsStore = useNotificationsStore();
const userStore = useUserStore();

// 下拉菜单可见状态
const dropdownVisible = ref(false);

// 详情弹窗状态
const detailModalVisible = ref(false);
const selectedNotification = ref<NotificationItem | null>(null);

// 轮询定时器
let pollTimer: ReturnType<typeof setInterval> | null = null;

// 监听登录状态，登录后开始轮询
watch(
  () => userStore.isLoggedIn,
  (isLoggedIn) => {
    if (isLoggedIn) {
      startPolling();
    } else {
      stopPolling();
      notificationsStore.reset();
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (userStore.isLoggedIn) {
    notificationsStore.fetchUnreadCount();
  }
});

/** 开始轮询未读数量 */
function startPolling() {
  // 立即获取一次
  notificationsStore.fetchUnreadCount();
  
  // 每 30 秒轮询一次
  pollTimer = setInterval(() => {
    notificationsStore.fetchUnreadCount();
  }, 30000);
}

/** 停止轮询 */
function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/** 下拉菜单可见性变化 */
function handleVisibleChange(visible: boolean) {
  dropdownVisible.value = visible;
  if (visible) {
    // 打开时加载通知列表
    notificationsStore.fetchNotifications(1);
  }
}

/** 获取通知图标 */
function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'feedback_reply':
      return MessageOutlined;
    case 'system':
      return NotificationOutlined;
    default:
      return BellOutlined;
  }
}

/** 获取通知类型样式类 */
function getNotificationClass(type: NotificationType) {
  switch (type) {
    case 'feedback_reply':
      return 'type-feedback';
    case 'system':
      return 'type-system';
    default:
      return '';
  }
}

/** 获取通知类型名称 */
function getNotificationTypeName(type: NotificationType) {
  switch (type) {
    case 'feedback_reply':
      return '反馈回复';
    case 'system':
      return '系统公告';
    default:
      return '通知';
  }
}

/** 格式化时间（相对时间） */
function formatTime(dateStr: string) {
  try {
    return formatDistanceToNow(new Date(dateStr), { 
      addSuffix: true, 
      locale: zhCN 
    });
  } catch {
    return dateStr;
  }
}

/** 格式化时间（完整时间） */
function formatFullTime(dateStr: string) {
  try {
    return format(new Date(dateStr), 'yyyy-MM-dd HH:mm:ss', { locale: zhCN });
  } catch {
    return dateStr;
  }
}

/** 点击通知项 - 打开详情弹窗 */
async function handleNotificationClick(item: NotificationItem) {
  // 标记为已读
  if (!item.isRead) {
    try {
      await notificationsStore.markRead(item.id);
    } catch (error) {
      // 忽略错误，继续显示详情
    }
  }
  
  // 关闭下拉菜单，打开详情弹窗
  dropdownVisible.value = false;
  selectedNotification.value = item;
  detailModalVisible.value = true;
}

/** 关闭详情弹窗 */
function closeDetailModal() {
  detailModalVisible.value = false;
  selectedNotification.value = null;
}

/** 从详情弹窗跳转到链接 */
function handleGoToLink() {
  if (selectedNotification.value?.link) {
    router.push(selectedNotification.value.link);
    closeDetailModal();
  }
}

/** 标记全部已读 */
async function handleMarkAllRead() {
  try {
    await notificationsStore.markAllRead();
    message.success('已全部标记为已读');
  } catch (error) {
    message.error('操作失败');
  }
}

/** 删除通知 */
async function handleDelete(item: NotificationItem, event: Event) {
  event.stopPropagation();
  try {
    await notificationsStore.removeNotification(item.id);
    message.success('已删除');
  } catch (error) {
    message.error('删除失败');
  }
}
</script>

<template>
  <Dropdown
    v-model:open="dropdownVisible"
    :trigger="['click']"
    placement="bottomRight"
    @openChange="handleVisibleChange"
  >
    <!-- 铃铛图标 -->
    <div class="notification-trigger">
      <Badge :count="notificationsStore.unreadCount" :overflow-count="99">
        <BellOutlined class="bell-icon" />
      </Badge>
    </div>
    
    <!-- 下拉内容 -->
    <template #overlay>
      <div class="notification-dropdown">
        <!-- 头部 -->
        <div class="dropdown-header">
          <span class="header-title">通知</span>
          <Button 
            v-if="notificationsStore.hasUnread"
            type="link" 
            size="small"
            @click="handleMarkAllRead"
          >
            <CheckOutlined />
            全部已读
          </Button>
        </div>
        
        <!-- 通知列表 -->
        <div class="dropdown-content">
          <Spin :spinning="notificationsStore.loading">
            <List
              v-if="notificationsStore.notifications.length > 0"
              :data-source="notificationsStore.notifications"
              class="notification-list"
            >
              <template #renderItem="{ item }">
                <List.Item 
                  class="notification-item"
                  :class="{ unread: !item.isRead }"
                  @click="handleNotificationClick(item)"
                >
                  <div class="item-content">
                    <div class="item-icon" :class="getNotificationClass(item.type)">
                      <component :is="getNotificationIcon(item.type)" />
                    </div>
                    <div class="item-main">
                      <div class="item-title">{{ item.title }}</div>
                      <div v-if="item.content" class="item-desc">{{ item.content }}</div>
                      <div class="item-time">{{ formatTime(item.createdAt) }}</div>
                    </div>
                    <Button 
                      type="text" 
                      size="small" 
                      class="item-delete"
                      @click="handleDelete(item, $event)"
                    >
                      <DeleteOutlined />
                    </Button>
                  </div>
                </List.Item>
              </template>
            </List>
            
            <Empty 
              v-else 
              :image="Empty.PRESENTED_IMAGE_SIMPLE" 
              description="暂无通知"
              class="empty-state"
            />
          </Spin>
        </div>
        
        <!-- 底部 -->
        <div v-if="notificationsStore.total > notificationsStore.pageSize" class="dropdown-footer">
          <Button type="link" block @click="dropdownVisible = false; router.push('/notifications')">
            查看全部通知
          </Button>
        </div>
      </div>
    </template>
  </Dropdown>
  
  <!-- 通知详情弹窗 -->
  <Modal
    v-model:open="detailModalVisible"
    :title="null"
    :footer="null"
    width="480px"
    centered
    class="notification-detail-modal"
    @cancel="closeDetailModal"
  >
    <div v-if="selectedNotification" class="detail-content">
      <!-- 类型标签 -->
      <div class="detail-type" :class="getNotificationClass(selectedNotification.type)">
        <component :is="getNotificationIcon(selectedNotification.type)" />
        <span>{{ getNotificationTypeName(selectedNotification.type) }}</span>
      </div>
      
      <!-- 标题 -->
      <h3 class="detail-title">{{ selectedNotification.title }}</h3>
      
      <!-- 时间 -->
      <div class="detail-time">{{ formatFullTime(selectedNotification.createdAt) }}</div>
      
      <!-- 内容 -->
      <div v-if="selectedNotification.content" class="detail-body">
        {{ selectedNotification.content }}
      </div>
      
      <!-- 操作按钮 -->
      <div class="detail-actions">
        <Button v-if="selectedNotification.link" type="primary" @click="handleGoToLink">
          查看详情
        </Button>
        <Button @click="closeDetailModal">关闭</Button>
      </div>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.notification-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
  }
  
  .bell-icon {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.75);
    transition: color 0.2s;
  }
  
  &:hover .bell-icon {
    color: #ffffff;
  }
  
  :deep(.ant-badge-count) {
    background-color: #ff4d4f;
    box-shadow: 0 0 0 2px #141414;
  }
}

.notification-dropdown {
  width: 360px;
  max-height: 480px;
  background-color: #1f1f1f;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}

.dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  
  .header-title {
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
  }
  
  :deep(.ant-btn-link) {
    color: rgba(255, 255, 255, 0.65);
    font-size: 13px;
    padding: 0;
    height: auto;
    
    &:hover {
      color: #277be8;
    }
  }
}

.dropdown-content {
  max-height: 360px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
  }
}

.notification-list {
  :deep(.ant-list-item) {
    padding: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
    
    &:last-child {
      border-bottom: none;
    }
  }
}

.notification-item {
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.04);
  }
  
  &.unread {
    background-color: rgba(39, 123, 232, 0.08);
    
    &:hover {
      background-color: rgba(39, 123, 232, 0.12);
    }
  }
  
  .item-content {
    display: flex;
    align-items: flex-start;
    padding: 12px 16px;
    width: 100%;
  }
  
  .item-icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    margin-right: 12px;
    font-size: 16px;
    
    &.type-feedback {
      background-color: rgba(39, 123, 232, 0.15);
      color: #277be8;
    }
    
    &.type-system {
      background-color: rgba(250, 173, 20, 0.15);
      color: #faad14;
    }
  }
  
  .item-main {
    flex: 1;
    min-width: 0;
  }
  
  .item-title {
    font-size: 14px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.4;
    margin-bottom: 4px;
    /* 标题最多1行，超出显示... */
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .item-desc {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.55);
    line-height: 1.4;
    margin-bottom: 4px;
    /* 内容最多2行，超出显示... */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .item-time {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.35);
  }
  
  .item-delete {
    flex-shrink: 0;
    opacity: 0;
    color: rgba(255, 255, 255, 0.45);
    transition: opacity 0.2s, color 0.2s;
    
    &:hover {
      color: #ff4d4f;
    }
  }
  
  &:hover .item-delete {
    opacity: 1;
  }
}

.empty-state {
  padding: 40px 20px;
  
  :deep(.ant-empty-description) {
    color: rgba(255, 255, 255, 0.45);
  }
}

.dropdown-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  
  :deep(.ant-btn-link) {
    color: rgba(255, 255, 255, 0.65);
    font-size: 13px;
    height: 44px;
    
    &:hover {
      color: #277be8;
    }
  }
}

/* 通知详情弹窗样式 */
.notification-detail-modal {
  :deep(.ant-modal-content) {
    background-color: #1f1f1f;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
  }
  
  :deep(.ant-modal-body) {
    padding: 24px;
  }
  
  :deep(.ant-modal-close) {
    color: rgba(255, 255, 255, 0.45);
    
    &:hover {
      color: rgba(255, 255, 255, 0.85);
    }
  }
}

.detail-content {
  .detail-type {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    margin-bottom: 16px;
    
    &.type-feedback {
      background-color: rgba(39, 123, 232, 0.15);
      color: #277be8;
    }
    
    &.type-system {
      background-color: rgba(250, 173, 20, 0.15);
      color: #faad14;
    }
  }
  
  .detail-title {
    font-size: 18px;
    font-weight: 600;
    color: #ffffff;
    margin: 0 0 8px 0;
    line-height: 1.4;
  }
  
  .detail-time {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.45);
    margin-bottom: 20px;
  }
  
  .detail-body {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.7;
    white-space: pre-wrap;
    word-break: break-word;
    padding: 16px;
    background-color: rgba(255, 255, 255, 0.04);
    border-radius: 8px;
    margin-bottom: 24px;
    max-height: 300px;
    overflow-y: auto;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-thumb {
      background-color: rgba(255, 255, 255, 0.15);
      border-radius: 3px;
    }
  }
  
  .detail-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    
    :deep(.ant-btn) {
      min-width: 80px;
    }
  }
}
</style>
